import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import { GradientTextAction } from 'src/common/GradientText';
import { SparkleSvg, RefreshSvg, CarbonFlowSvg } from 'src/assets/dynamicSvg';
import useVariables from 'src/hooks/useVariables';
import { VariablesWorkspaceModal } from 'src/components/modals/VariablesWorkspaceModal';
import { useRiskIntel } from '../useRiskIntel';
import { useLoadingAction } from '../useLoadingAction';
import { ImpactFormulaCard } from '../comps/ImpactFormulaCard';
import { PrimaryButton, SecondaryButton } from '../comps/SuggestionButtons';
import { StatusPill } from '../comps/StatusPill';
import { VariablesPopover } from '../comps/VariablesPopover';

const Card = styled(PageCard)`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  border-radius: 8px;
  padding: 0;
  overflow: visible;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
`;

const EyebrowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const VariablesAnchor = styled.div`
  position: relative;
`;

const VariablesTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }
`;

// Viewport-anchored so it isn't clipped by the modal's overflow.
const FixedPopover = styled.div`
  position: fixed;
  z-index: 1000;
`;

const POPOVER_WIDTH = 600;
const MARGIN = 12;

interface PopoverPos {
  top?: number;
  bottom?: number;
  left: number;
  maxHeight: number;
}

// Anchor below the trigger when there's room, else flip above; clamp into the viewport and cap the
// height to the available space so the (internally-scrolling) card never runs off-screen.
const positionFor = (el: HTMLElement): PopoverPos => {
  const rect = el.getBoundingClientRect();
  const width = Math.min(POPOVER_WIDTH, window.innerWidth - 2 * MARGIN);
  const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - width - MARGIN));
  const spaceBelow = window.innerHeight - rect.bottom - 8 - MARGIN;
  const spaceAbove = rect.top - 8 - MARGIN;
  if (spaceBelow >= 320 || spaceBelow >= spaceAbove) {
    return { top: rect.bottom + 8, left, maxHeight: Math.max(180, spaceBelow) };
  }
  return { bottom: window.innerHeight - rect.top + 8, left, maxHeight: Math.max(180, spaceAbove) };
};

const Footer = styled.div`
  display: flex;
  gap: 32px;
  padding: 24px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.stroke};

  & > * {
    flex: 1 1 0;
  }
`;

export const ImpactFormulaSection: React.FC = () => {
  const { session, pending, acceptImpacts, changeImpacts, populateVariable, assessmentId, assessmentName } =
    useRiskIntel();
  const { loadingKey, run } = useLoadingAction();
  const variables = session?.variables ?? [];
  const impacts = (session?.impacts ?? []).filter((impact) => impact.selected && impact.formula);
  const accepted = !!session?.impactsAccepted;

  // Only to refresh the variable cache after the "add variable" modal closes.
  const { invalidate } = useVariables({ assessmentId: assessmentId ?? '', wvOverride: true });

  const [variablesOpen, setVariablesOpen] = useState(false);
  const [editVarId, setEditVarId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pos, setPos] = useState<PopoverPos | null>(null);
  const [variablesModalState, setVariablesModalState] = useState<null | { scope: 'workspace' | 'assessment' }>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const openVariables = (el: HTMLElement, editId?: number) => {
    setEditVarId(editId ?? null);
    setAnchorEl(el);
    setPos(positionFor(el));
    setVariablesOpen(true);
  };

  const closeVariables = () => {
    setVariablesOpen(false);
    setEditVarId(null);
    setAnchorEl(null);
    setPos(null);
  };

  useEffect(() => {
    if (!variablesOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      closeVariables();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [variablesOpen]);

  // Reposition on scroll/resize; capture-phase 'scroll' catches whichever inner container scrolls.
  useEffect(() => {
    if (!variablesOpen || !anchorEl) return undefined;
    const update = () => {
      const rect = anchorEl.getBoundingClientRect();
      // Trigger scrolled out of view → close (no orphaned popover floating over the modal).
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setVariablesOpen(false);
        setAnchorEl(null);
        setPos(null);
        return;
      }
      setPos(positionFor(anchorEl));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [variablesOpen, anchorEl]);

  if (impacts.length === 0) return null;

  return (
    <>
    <Card>
      <Eyebrow>
        <EyebrowLabel>
          <SparkleSvg size={16} />
          <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
            Suggested Impact Formula
          </Poppins>
        </EyebrowLabel>

        <VariablesAnchor ref={anchorRef}>
          <VariablesTrigger
            type="button"
            onClick={(e) => (variablesOpen ? closeVariables() : openVariables(e.currentTarget))}
          >
            <CarbonFlowSvg size={16} />
            <GradientTextAction px={14}>Variables</GradientTextAction>
          </VariablesTrigger>
        </VariablesAnchor>
      </Eyebrow>

      {impacts.map((impact, idx) => (
        <ImpactFormulaCard
          key={impact.id}
          impact={impact}
          variables={variables}
          first={idx === 0}
          onVariableClick={(id, el) => openVariables(el, id)}
        />
      ))}

      <Footer>
        {accepted ? (
          <StatusPill label="Accepted" />
        ) : (
          <PrimaryButton
            label="Accept suggestion"
            fullWidth
            loading={loadingKey === 'accept'}
            disabled={pending}
            onClick={() => run('accept', () => acceptImpacts())}
          />
        )}
        <SecondaryButton
          label="Change relevant impacts"
          icon={<RefreshSvg size={18} style={{ marginRight: 8 }} />}
          fullWidth
          loading={loadingKey === 'change'}
          disabled={pending}
          onClick={() => run('change', () => changeImpacts())}
        />
      </Footer>
    </Card>
    {variablesOpen && (
      <FixedPopover
        ref={popoverRef}
        style={pos ? { top: pos.top, bottom: pos.bottom, left: pos.left } : undefined}
      >
        <VariablesPopover
          variables={variables}
          pending={pending}
          maxHeight={pos?.maxHeight}
          onPopulate={(id, value, currency) => populateVariable(id, value, currency)}
          onAddWorkspaceVariable={() => setVariablesModalState({ scope: 'workspace' })}
          onAddAssessmentVariable={() => setVariablesModalState({ scope: 'assessment' })}
          initialEditId={editVarId}
        />
      </FixedPopover>
    )}
    {variablesModalState && (
      <VariablesWorkspaceModal
        onClose={() => {
          invalidate();
          setVariablesModalState(null);
        }}
        scope={
          variablesModalState.scope === 'workspace'
            ? { type: 'workspace' }
            : { type: 'assessment', id: assessmentId ?? '', name: assessmentName ?? '' }
        }
        initHideEmptyVariables={false}
      />
    )}
    </>
  );
};
